import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { locations } from '../data/locationsData';

const { FiMenu, FiX, FiChevronDown, FiMapPin, FiEdit } = FiIcons;

const LocationDropdown = ({ onClose }) => (
  <div className="absolute top-full left-1/2 -translate-x-1/2 mt-2 w-[600px] bg-white border border-gray-100 shadow-2xl z-50 rounded-xl overflow-hidden">
    <div className="grid grid-cols-3 gap-1 p-3">
      {locations.map((loc) => (
        <Link key={loc.slug} to={`/location/${loc.slug}`} onClick={onClose}
          className="flex items-center gap-2 px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-red-50 hover:text-primary transition-colors rounded-lg">
          <FiMapPin className="w-3.5 h-3.5 text-primary flex-shrink-0" />
          <span className="truncate">{loc.label}</span>
        </Link>
      ))}
    </div>
  </div>
);

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isLocationOpen, setIsLocationOpen] = useState(false);
  const [isMobileLocationOpen, setIsMobileLocationOpen] = useState(false);
  const locationRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (locationRef.current && !locationRef.current.contains(e.target)) {
        setIsLocationOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleNavClick = (path, hash) => {
    setIsMenuOpen(false);
    if (path) {
      navigate(path);
      window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
    } else if (hash) {
      if (location.pathname !== '/') {
        navigate('/');
        setTimeout(() => { document.getElementById(hash)?.scrollIntoView({ behavior: 'smooth' }); }, 100);
      } else {
        document.getElementById(hash)?.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };

  return (
    <header className="bg-white shadow-sm py-3 z-50 relative">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-14">
          {/* Logo */}
          <Link to="/" className="flex items-center cursor-pointer">
            <img 
              src="https://quest-media-storage-bucket.s3.us-east-2.amazonaws.com/1773836886799-image.png" 
              alt="MyHomeTutors Logo" 
              className="h-9 md:h-11 object-contain" 
            />
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden lg:flex items-center gap-7">
            <button onClick={() => handleNavClick('/')}
              className="text-sm font-semibold text-gray-600 hover:text-primary transition-colors relative group bg-transparent border-none cursor-pointer">
              Home
              <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-primary transition-all group-hover:w-full"></span>
            </button>
            <button onClick={() => handleNavClick('/tutors')}
              className="text-sm font-semibold text-gray-600 hover:text-primary transition-colors relative group bg-transparent border-none cursor-pointer">
              Our Tutors
              <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-primary transition-all group-hover:w-full"></span>
            </button>
            <div className="relative" ref={locationRef}>
              <button onClick={() => setIsLocationOpen(!isLocationOpen)}
                className="flex items-center gap-1 text-sm font-semibold text-gray-600 hover:text-primary transition-colors bg-transparent border-none cursor-pointer">
                Locations
                <FiChevronDown className={`w-4 h-4 transition-transform duration-200 ${isLocationOpen ? 'rotate-180' : ''}`} />
              </button>
              {isLocationOpen && <LocationDropdown onClose={() => setIsLocationOpen(false)} />}
            </div>
            <Link to="/about"
              className="text-sm font-semibold text-gray-600 hover:text-primary transition-colors relative group">
              About Us
              <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-primary transition-all group-hover:w-full"></span>
            </Link>
            <Link to="/become-a-tutor"
              className="flex items-center gap-1.5 text-sm font-semibold text-gray-600 hover:text-primary transition-colors relative group">
              <FiEdit className="w-3.5 h-3.5" /> Become a Tutor
              <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-primary transition-all group-hover:w-full"></span>
            </Link>
          </nav>

          {/* CTA */}
          <div className="hidden lg:flex items-center gap-3">
            <a href="tel:+919415905584"
              className="flex items-center gap-2 bg-primary text-white px-5 py-2.5 font-bold text-sm hover:bg-primary-hover transition-all shadow-md">
              <SafeIcon icon={FiIcons.FiPhoneCall} className="w-4 h-4" />
              Talk to a Tutor
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button onClick={() => setIsMenuOpen(!isMenuOpen)} className="lg:hidden text-gray-700 hover:text-primary p-2 focus:outline-none">
            {isMenuOpen ? <FiX className="h-6 w-6" /> : <FiMenu className="h-6 w-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Nav */}
      {isMenuOpen && (
        <div className="lg:hidden bg-white border-t border-gray-100 absolute w-full left-0 top-full shadow-xl z-50">
          <div className="px-4 pt-3 pb-5 space-y-1">
            <button onClick={() => handleNavClick('/')}
              className="flex items-center gap-2 px-3 py-3 text-base font-semibold text-gray-700 hover:text-primary hover:bg-red-50 transition w-full text-left bg-transparent border-none">
              Home
            </button>
            <button onClick={() => handleNavClick('/tutors')}
              className="flex items-center gap-2 px-3 py-3 text-base font-semibold text-gray-700 hover:text-primary hover:bg-red-50 transition w-full text-left bg-transparent border-none">
              Our Tutors
            </button>
            <div>
              <button onClick={() => setIsMobileLocationOpen(!isMobileLocationOpen)}
                className="flex items-center justify-between gap-2 px-3 py-3 text-base font-semibold text-gray-700 hover:text-primary hover:bg-red-50 transition w-full text-left bg-transparent border-none">
                <span className="flex items-center gap-2"><FiMapPin className="w-4 h-4" /> Locations</span>
                <FiChevronDown className={`w-4 h-4 transition-transform ${isMobileLocationOpen ? 'rotate-180' : ''}`} />
              </button>
              {isMobileLocationOpen && (
                <div className="ml-4 border-l-2 border-red-100 pl-3 space-y-1 mb-1 grid grid-cols-1 sm:grid-cols-2 gap-1">
                  {locations.map((loc) => (
                    <Link key={loc.slug} to={`/location/${loc.slug}`} onClick={() => setIsMenuOpen(false)}
                      className="flex items-center gap-2 px-3 py-2 text-sm font-semibold text-gray-600 hover:text-primary hover:bg-red-50 transition rounded-lg">
                      <FiMapPin className="w-3 h-3 text-primary" />
                      <span className="truncate">{loc.label}</span>
                    </Link>
                  ))}
                </div>
              )}
            </div>
            <Link to="/about" onClick={() => setIsMenuOpen(false)}
              className="flex items-center gap-2 px-3 py-3 text-base font-semibold text-gray-700 hover:text-primary hover:bg-red-50 transition">
              About Us
            </Link>
            <Link to="/become-a-tutor" onClick={() => setIsMenuOpen(false)}
              className="flex items-center gap-2 px-3 py-3 text-base font-semibold text-gray-700 hover:text-primary hover:bg-red-50 transition">
              <FiEdit className="w-4 h-4" /> Become a Tutor
            </Link>
            <Link to="/contact" onClick={() => setIsMenuOpen(false)}
              className="flex items-center gap-2 px-3 py-3 text-base font-semibold text-gray-700 hover:text-primary hover:bg-red-50 transition">
              Contact Us
            </Link>
            <div className="pt-2">
              <a href="tel:+919415905584"
                className="flex items-center justify-center gap-2 w-full bg-primary text-white px-5 py-3 font-bold text-sm hover:bg-primary-hover transition shadow-md">
                <SafeIcon icon={FiIcons.FiPhoneCall} className="w-4 h-4" />
                Talk to a Tutor
              </a>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;